import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { format, set, subDays } from "date-fns";
import { debounce } from "lodash";
import { Plus, X } from "lucide-react";

import { Input } from "@components/ui/Input";

import { Loading } from "./components/layout/Loading";
import { DatePicker } from "./components/ui/datepicker/DatePicker";
import { Pagination } from "./components/ui/pagination/Pagination";
import {
  setSortState,
  SortTable,
  TableSort,
} from "./components/ui/table/SortTable";
import { Table } from "./components/ui/table/Table";
import {
  apiIgnoredNoticeFilter,
  formatReceivedOn,
  NOTICE_TYPE_LABELS,
  noticeTypeLabel,
} from "./queries/users/notices";
import type { User } from "./queries/users/types";
import {
  type AdminUserListItem,
  NOTICE_TYPES,
  type NoticeFilter,
  searchAdminUsers,
} from "./queries/users/userApi";

const USERS_PAGINATION = 20;

type FormValues = {
  query: string;
  createdDateField: string;
  dateFrom: Date;
  dateTo: Date;
  // "" = no notice filter (all users).
  noticeType: NoticeFilter | "";
};

// Only email + createdAt are sortable (id / status don't make sense
// to sort by, and the omg-api list endpoint only supports these two).
type SortOrder = {
  [Property in keyof Pick<User, "email" | "createdAt">]: TableSort;
};

export const UserList = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<AdminUserListItem[] | undefined>();
  const [userCount, setUserCount] = useState(0);
  // Deploy-order guard, see apiIgnoredNoticeFilter.
  const [noticeFilterIgnored, setNoticeFilterIgnored] = useState(false);
  const latestSearch = useRef(0);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(userCount / USERS_PAGINATION);

  const [sort, setSort] = useState<SortOrder>({
    email: TableSort.NONE,
    createdAt: TableSort.DESC,
  });

  const { register, handleSubmit, watch, setValue, resetField } =
    useForm<FormValues>({
      defaultValues: {
        query: "",
        createdDateField: "",
        dateFrom: subDays(new Date(), 7),
        dateTo: new Date(),
        noticeType: "",
      },
    });

  const formValues = watch();

  const viewUser = (id: string) => {
    navigate(id);
  };

  const createNewUser = () => {
    navigate("new");
  };

  const searchUsers = async (
    data: FormValues,
    page: number = currentPage,
    currentSort: SortOrder = sort,
  ) => {
    // Only the latest search may render: with a notice filter, an older
    // unfiltered response landing last would show unflagged users under the
    // filter (and the guard below, computed for that response, would agree).
    const seq = ++latestSearch.current;
    // The active sort column (only one at a time); default to createdAt desc.
    const sortField = (
      Object.keys(currentSort) as (keyof typeof currentSort)[]
    ).find((key) => currentSort[key] !== TableSort.NONE);
    const sortBy = sortField ?? "createdAt";
    const sortOrder =
      (sortField ? currentSort[sortField] : TableSort.DESC) === TableSort.ASC
        ? "asc"
        : "desc";

    const query = data.query.trim().toLowerCase();
    const hasDateRange = !!data.createdDateField;
    const noticeType = data.noticeType || undefined;

    const response = await searchAdminUsers({
      email: query || undefined,
      noticeType,
      sortBy,
      sortOrder,
      ...(hasDateRange && {
        createdAtFrom: data.dateFrom.toISOString(),
        createdAtTo: set(data.dateTo, {
          hours: 23,
          minutes: 59,
          seconds: 59,
          milliseconds: 999,
        }).toISOString(),
      }),
      limit: USERS_PAGINATION,
      offset: (page - 1) * USERS_PAGINATION,
    });

    if (seq !== latestSearch.current) return;

    setUsers(response.users);
    setUserCount(response.total);
    setNoticeFilterIgnored(apiIgnoredNoticeFilter(response.users, noticeType));
    setLoading(false);
  };

  const debouncedSearch = useRef(
    debounce((formData: FormValues, page: number, currentSort: SortOrder) => {
      searchUsers(formData, page, currentSort);
    }, 500),
  ).current;

  useEffect(() => {
    setLoading(true);
    debouncedSearch(formValues, currentPage, sort);

    return () => {
      debouncedSearch.cancel();
    };
  }, [
    formValues.query,
    formValues.createdDateField,
    formValues.dateFrom,
    formValues.dateTo,
    formValues.noticeType,
    sort,
    currentPage,
    debouncedSearch,
  ]);

  // A new notice filter starts from page 1; page 5 of "all users" is usually
  // past the end of a short flagged list.
  useEffect(() => {
    setCurrentPage(1);
  }, [formValues.noticeType]);

  useEffect(() => {
    if (formValues.createdDateField === "-7d") {
      setValue("dateFrom", subDays(new Date(), 7));
      setValue("dateTo", new Date());
    } else if (formValues.createdDateField === "-30d") {
      setValue("dateFrom", subDays(new Date(), 30));
      setValue("dateTo", new Date());
    }
  }, [formValues.createdDateField, setValue]);

  const onSubmit = (data: FormValues) => {
    debouncedSearch.cancel();
    searchUsers(data);
  };

  const handleSortClick = (field: keyof typeof sort) => {
    setSort((prevSort) => {
      const newSort = setSortState(prevSort, field);

      setTimeout(() => {
        debouncedSearch.cancel();
        searchUsers(formValues, currentPage, newSort);
      }, 0);

      return newSort;
    });
  };

  return (
    <>
      <div className="flex items-center justify-between py-4">
        <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
        <button
          onClick={createNewUser}
          className="flex items-center gap-2 px-4 py-2"
        >
          <Plus className="w-4 h-4" />
          Create New User
        </button>
      </div>

      <div className="flex py-4">
        <form className="flex w-full gap-4" onSubmit={handleSubmit(onSubmit)}>
          <Input
            type="search"
            className="flex-grow"
            placeholder="Search by email..."
            {...register("query")}
          />

          <select
            aria-label="Notice on file"
            className="min-h-10 rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm"
            {...register("noticeType")}
          >
            <option value="">All users</option>
            <option value="any">Any notice on file</option>
            {NOTICE_TYPES.map((type) => (
              <option key={type} value={type}>
                Notice: {NOTICE_TYPE_LABELS[type]}
              </option>
            ))}
          </select>

          <div className="w-full lg:w-[40%] flex flex-col">
            <select
              className="min-h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
              {...register("createdDateField")}
            >
              <option value="">All time</option>
              <option value="-7d">Last 7 days</option>
              <option value="-30d">Last 30 days</option>
              <option value="custom">Custom Range</option>
            </select>

            <div
              className={`${formValues.createdDateField !== "custom" ? "hidden" : "flex gap-2 w-full"}`}
            >
              <DatePicker
                label="From"
                name="dateFrom"
                selected={formValues.dateFrom}
                onSelect={(date) => setValue("dateFrom", date!)}
                placeholder="From..."
                register={() => register("dateFrom")}
              />
              <DatePicker
                label="To"
                name="dateTo"
                selected={formValues.dateTo}
                onSelect={(date) => setValue("dateTo", date!)}
                placeholder="To..."
                register={() => register("dateTo")}
              />
            </div>
          </div>
          {formValues.createdDateField && (
            <button
              className="flex-auto"
              onClick={() => resetField("createdDateField")}
            >
              <X />
            </button>
          )}
        </form>
      </div>

      <div className="">
        {noticeFilterIgnored && (
          <p className="text-red-600 mb-2">
            Can&apos;t filter by notice: the API returned users without the
            requested notice, which means it doesn&apos;t support the notice
            filter yet, so no rows are shown. This list needs omg-api with
            OMG-2558 deployed.
          </p>
        )}
        <Table>
          <thead>
            <tr>
              <th style={{ width: "350px" }}>ID</th>
              <th
                style={{ width: "400px" }}
                onClick={() => handleSortClick("email")}
              >
                <SortTable title="Email" direction={sort.email} />
              </th>
              <th style={{ width: "120px" }}>Status</th>
              <th
                style={{ width: "100px" }}
                onClick={() => handleSortClick("createdAt")}
              >
                <SortTable title="Created" direction={sort.createdAt} />
              </th>
              <th style={{ width: "260px" }}>Notice on file</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: "14px", lineHeight: "16px" }}>
            {/* Never render an unfiltered list as the flagged users. */}
            {!noticeFilterIgnored &&
              users?.map((user) => (
                <tr key={user.id} onClick={() => viewUser(user.id)}>
                  <td>{user.id}</td>
                  <td>{user.email}</td>
                  <td>{user.status}</td>
                  <td>{format(user.createdAt, "M/d/yyyy")}</td>
                  <td>
                    {user.notices?.map((notice) => (
                      <div key={notice.noteId}>
                        {noticeTypeLabel(notice.type)} (
                        {formatReceivedOn(notice.receivedOn)})
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
        {loading || !users ? (
          <Loading />
        ) : noticeFilterIgnored ? null : users?.length ? (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
          />
        ) : (
          <div className="flex items-center justify-center w-full p-[4rem]">
            <span className="font-bold text-[24px]">No users found.</span>
          </div>
        )}
      </div>
    </>
  );
};
